'use client';

import React, { ReactNode, useEffect, useState } from 'react';
import throttledWidthObserver from '../../../app/lib/observers/throttled-width-observer';
import {
    FreeMode,
    Keyboard,
    Mousewheel,
    Navigation,
    Scrollbar,
} from 'swiper/modules';
import { Swiper } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';
import 'swiper/css/scrollbar';
import './scrollbar-swiper-styles.css';

interface ScrollBarSwiperProps {
    children: any;
    loadingSkeleton?: ReactNode;
    className: string;
    slidePerViewInMobile?: number | 'auto';
    slidePerViewInDesktop?: number | 'auto';
    spaceBetweenDesktop?: number;
    spaceBetweenMobile?: number;
    swiperIconNextClassName?: string;
    swiperIconPrevClassName?: string;
    hasScrollbar?: boolean;
    direction?: 'horizontal' | 'vertical';
    sidebar?: boolean;
}

export const ScrollBarSwiper = ({
    children,
    loadingSkeleton,
    className,
    slidePerViewInMobile,
    slidePerViewInDesktop,
    spaceBetweenDesktop,
    spaceBetweenMobile,
    swiperIconNextClassName,
    swiperIconPrevClassName,
    hasScrollbar = true,
    direction = 'horizontal',
}: ScrollBarSwiperProps) => {
    const [initialised, setInitialised] = useState(null);
    const clientWidth =
        typeof document !== 'undefined' ? document.body.clientWidth : 1280;
    const [windowSize, setWindowSize] = useState<number>(clientWidth);

    useEffect(() => {
        if ((document as any) === 'undefined') return;
        const observer = throttledWidthObserver(
            document.body,
            setWindowSize,
            100
        );
        observer.observe(document.body);
        return () => observer.unobserve(document.body);
    }, []);

    const handleSwiperReady = (swiper) => {
        setInitialised(swiper);
        // Code to execute after Swiper initialization
    };

    let content = children;
    if (loadingSkeleton && !initialised) {
        content = loadingSkeleton;
    }

    // content = loadingSkeleton;

    return (
        <div className="swiperContainer">
            <Swiper
                onSwiper={handleSwiperReady}
                direction={direction}
                navigation={{
                    nextEl: `.${swiperIconNextClassName}`,
                    prevEl: `.${swiperIconPrevClassName}`,
                    disabledClass: 'swiper-icon-disabled',
                }}
                observer={true}
                freeMode={true}
                // cssMode={windowSize < 768}
                scrollbar={hasScrollbar}
                mousewheel={{ forceToAxis: true }}
                keyboard={true}
                modules={[
                    Navigation,
                    Scrollbar,
                    FreeMode,
                    Keyboard,
                    Mousewheel,
                ]}
                className={`${className} scrollbar-swiper`}
                slidesOffsetAfter={windowSize > 991 ? 72 : 24}
                slidesOffsetBefore={windowSize > 991 ? 72 : 24}
                breakpoints={{
                    320: {
                        slidesPerView: slidePerViewInMobile
                            ? slidePerViewInMobile
                            : 1.12,
                        spaceBetween: spaceBetweenMobile,
                    },
                    768: {
                        slidesPerView: slidePerViewInMobile
                            ? slidePerViewInMobile
                            : 2.1,
                        spaceBetween: spaceBetweenMobile,
                    },
                    1024: {
                        slidesPerView: slidePerViewInDesktop
                            ? slidePerViewInDesktop
                            : 2.9,
                        spaceBetween: spaceBetweenDesktop,
                    },
                }}
            >
                {content}
            </Swiper>
        </div>
    );
};

export default ScrollBarSwiper;
